import Table, { TableBody, TableHeader } from '@ingka/table';
import Text from '@ingka/text';
import {
  useFormatWithLocale,
  useNavigateWithLocale,
} from '@lotta/self-service/lottery-fe/feature-hooks';
import type { LotteryDraw } from '@lotta/shared/types-lottery-schemas';
import { useTranslation } from 'react-i18next';
import styles from './CompletedDrawsTable.module.scss';

type CompletedDrawsInfoTableProps = {
  completedDraws: LotteryDraw[];
};

const TableRows: React.FC<{ completedDraws: LotteryDraw[]; drawTranslation: string }> = ({
  completedDraws,
  drawTranslation,
}) => {
  const { dateWithLocale, numberWithLocale } = useFormatWithLocale();
  const navigate = useNavigateWithLocale();

  return (
    <>
      {completedDraws.map((completedDraw, index) => {
        const { startDate, endDate, createdAt, nbrParticipants, nbrWinners, id } = completedDraw;
        return (
          <tr
            className={styles['draw-table-row']}
            key={id}
            onClick={() => {
              navigate(`draw/${id}`);
            }}
          >
            <td>
              <Text className={styles['row-item-name']}>{`${drawTranslation} ${
                completedDraws.length - index
              }`}</Text>
            </td>
            <td>
              <Text>{dateWithLocale(startDate)}</Text>
            </td>
            <td>
              <Text>{dateWithLocale(endDate)}</Text>
            </td>
            <td data-testid="completed-draw-date">
              <Text>{dateWithLocale(createdAt)}</Text>
            </td>
            <td>
              <Text>{numberWithLocale(nbrParticipants)}</Text>
            </td>
            <td>
              <Text>{numberWithLocale(nbrWinners)}</Text>
            </td>
          </tr>
        );
      })}
    </>
  );
};

export const CompletedDrawsTable: React.FC<CompletedDrawsInfoTableProps> = ({ completedDraws }) => {
  const { t } = useTranslation();
  const drawTranslation = t('general-draw');

  // Sort by draw date descending
  const sortedDraws = [...completedDraws].sort(
    (a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
  );

  return (
    <Table fullWidth inset>
      <TableHeader sticky={true}>
        <tr>
          <th>
            <Text bodySize="s">{t('general-name')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('general-start-date')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('general-end-date')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('completed-draws-table-draw-date')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('completed-draws-table-participants')}</Text>
          </th>
          <th>
            <Text bodySize="s">{t('completed-draws-table-nbr-winners')}</Text>
          </th>
        </tr>
      </TableHeader>
      <TableBody striped data-testid="draws-table-body">
        <TableRows completedDraws={sortedDraws} drawTranslation={drawTranslation} />
      </TableBody>
    </Table>
  );
};
